import { useContext, useRef, useState } from "react";
import { UserContext } from "contexts/UserContext";
import { useNavigate } from "react-router-dom";

// material-ui
import useMediaQuery from "@mui/material/useMediaQuery";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";
import ListItemIcon from "@mui/material/ListItemIcon";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Box from "@mui/material/Box";

// project imports
import IconButton from "components/@extended/IconButton";
import Transitions from "components/@extended/Transitions";
import MainCard from "components/MainCard";

import QuestionCircleOutlined from "@ant-design/icons/QuestionCircleOutlined";
import HomeOutlined from "@ant-design/icons/HomeOutlined";
import KeyOutlined from "@ant-design/icons/KeyOutlined";
import TeamOutlined from "@ant-design/icons/TeamOutlined";
import LockOutlined from "@ant-design/icons/LockOutlined";
import CommentOutlined from "@ant-design/icons/CommentOutlined";
import UnorderedListOutlined from "@ant-design/icons/UnorderedListOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import WalletOutlined from "@ant-design/icons/WalletOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Avatar from "components/@extended/Avatar";
import avatarHost from "assets/images/host2.png";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import CardContent from "@mui/material/CardContent";

export default function Setting() {
  const user = useContext(UserContext);
  const navigate = useNavigate();
  const downMD = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const anchorRef: any = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  if (!user) return <div>...</div>;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={() => ({
          color: "text.primary",
          bgcolor: open ? "grey.100" : "transparent",
        })}
        aria-label="open settings"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <SettingOutlined />
      </IconButton>
      <Popper
        placement={downMD ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            { name: "offset", options: { offset: [downMD ? -5 : 0, 9] } },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={downMD ? "top" : "top-right"}
            in={open}
            ref={null}
            {...TransitionProps}
          >
            <Paper
              sx={(theme: any) => ({
                boxShadow: theme.customShadows.z1,
                width: "100%",
                minWidth: 285,
                maxWidth: { xs: 285, md: 420 },
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  elevation={0}
                  border={false}
                  content={false}
                  title=""
                  boxShadow={undefined}
                  subheader={undefined}
                  darkTitle={undefined}
                  secondary={undefined}
                  shadow={undefined}
                  ref={undefined}
                >
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid
                      container
                      sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Grid>
                        <Stack
                          direction="row"
                          sx={{ gap: 1.25, alignItems: "center" }}
                        >
                          <Avatar
                            alt="profile host"
                            src={avatarHost}
                            sx={{ width: 32, height: 32 }}
                          />
                          <Stack>
                            <Typography variant="h5">Host</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user?.host_name}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <List
                    component="nav"
                    sx={{ p: 0, "& .MuiListItemIcon-root": { minWidth: 32 } }}
                  >
                    <Link
                      underline="none"
                      sx={{ color: "inherit" }}
                      onClick={() => {
                        navigate("/admin/showHost", {
                          state: { host_id: user?.host_id },
                        });
                        setOpen(false);
                      }}
                    >
                      <ListItemButton>
                        <ListItemIcon>
                          <SettingOutlined />
                        </ListItemIcon>
                        <ListItemText primary="Host Settings" />
                      </ListItemButton>
                    </Link>
                    <Link
                      underline="none"
                      sx={{ color: "inherit" }}
                      onClick={() => {
                        navigate("/admin/users");
                        setOpen(false);
                      }}
                    >
                      <ListItemButton>
                        <ListItemIcon>
                          <TeamOutlined />
                        </ListItemIcon>
                        <ListItemText primary="User List" />
                      </ListItemButton>
                    </Link>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
